// Fixed Term Contracts Expiration report constants (ADR-090)

/**
 * Sentinel value for the "Expires" filter's Overdue option.
 *
 * The numeric windows can't express "overdue only": per ADR-090 an overdue row
 * (negative daysUntilExpiry) matches EVERY "within N days" window by design, so
 * they are never hidden. "Within 30 days" therefore means overdue + the next 30
 * days. Overdue is a distinct predicate (daysUntilExpiry < 0), carried as its
 * own API param (overdueOnly) rather than a magic number in expiresWithinDays.
 */
export const EXPIRY_OVERDUE = 'overdue';

/**
 * Options for the "Expires" filter, ordered broadest → narrowest. Numeric
 * values map to the API's expiresWithinDays query param; EXPIRY_OVERDUE maps to
 * overdueOnly; empty string = no filter (all fixed term contracts).
 */
export const EXPIRY_WINDOW_OPTIONS = [
  { value: '', label: 'All' },
  { value: 90, label: 'Within 90 days' },
  { value: 60, label: 'Within 60 days' },
  { value: 30, label: 'Within 30 days' },
  { value: EXPIRY_OVERDUE, label: 'Overdue' },
];

/**
 * Human-readable contract duration in years/months (calendar-aware),
 * rounded to the NEAREST month: leftover days beyond the last full month
 * round up when they reach 15, otherwise round down. Sub-half-month
 * contracts fall back to days. Returns null when dates are missing or inverted.
 * e.g. 03/10/2022 → 15/10/2026 = "4 years" (12 leftover days round down);
 *      03/10/2022 → 20/10/2026 = "4 years 1 month" (17 leftover days round up).
 */
export const formatContractDuration = (startDate, endDate) => {
  if (!startDate || !endDate) { return null; }
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) { return null; }

  // Full calendar months between the dates
  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (end.getDate() < start.getDate()) { months -= 1; }

  // Leftover days beyond the last full month; >= 15 rounds up to the next month
  const anchor = new Date(start);
  anchor.setMonth(anchor.getMonth() + months);
  const leftoverDays = Math.round((end - anchor) / (1000 * 60 * 60 * 24));
  if (leftoverDays >= 15) { months += 1; }

  if (months === 0) {
    // Sub-half-month contract — days are more honest than "0 months"
    const days = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }

  const years = Math.floor(months / 12);
  const remMonths = months % 12;

  const parts = [];
  if (years > 0) { parts.push(`${years} ${years === 1 ? 'year' : 'years'}`); }
  if (remMonths > 0) { parts.push(`${remMonths} ${remMonths === 1 ? 'month' : 'months'}`); }

  return parts.join(' ');
};

/**
 * ADR-136 phase 4 (A2): the auto-populated fixed-term sentence. Generated at the
 * entry surfaces (hire/rehire forms) and editable there; it lands as BOTH the new
 * episode's comment and the scheduled end's resignation reason, and reuses
 * formatContractDuration so the sentence and the FTC report's Duration column can
 * never disagree. Dates render DD/MM/YYYY per the house convention. Returns null
 * when the range is missing or inverted (end <= start) — callers treat that as
 * "no auto-text" and block submit with their own validation.
 */
// (See employmentSourceSystems.toSourceContractId for how the contract number
// itself is stored — verbatim free text since the 2026-08-05 leading-zeros fix.)
// ADR-116 §3.3 — this sentence is PERSISTED to EmploymentEpisode.comments and is
// later matched by exact reconstruction in radar-api (ADR-146 §4.1), so its
// "— contract <N>" form must not change: a reformat would make stored comments
// match neither candidate and the regeneration would silently no-op. The
// `Contract · N` display formatter is deliberately not used here.
export const buildFixedTermComment = (startDate, endDate, contractNumber) => {
  const duration = formatContractDuration(startDate, endDate);
  if (!duration) { return null; }

  const toDdMmYyyy = (value) => {
    const d = new Date(value);
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getUTCFullYear()}`;
  };

  const base = `Fixed term contract for ${duration} (${toDdMmYyyy(startDate)} – ${toDdMmYyyy(endDate)})`;
  const number = contractNumber ? String(contractNumber).trim() : '';
  return number ? `${base} — contract ${number}` : base;
};

/**
 * Urgency color for the "in Xd" chip, keyed on days until contract expiry.
 * Negative = overdue (effective date passed, end not yet processed).
 */
export const getExpiryDaysColor = (days) => {
  if (days < 0) { return '#DC2626'; }   // overdue — red
  if (days <= 30) { return '#DC2626'; } // expiring within 30 days — red
  if (days <= 60) { return '#F59E0B'; } // 31–60 days — amber
  if (days <= 90) { return '#10B981'; } // 61–90 days — green
  return '#64748B';                     // beyond 90 days — neutral slate
};
